// src/pages/functions/FunctionCoolingHeatingDetailPage.js
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ProductGrid from "../../components/category_page/ProductGrid";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function FunctionCoolingHeatingDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToFilteredProducts = () =>
    navigate("/kategoriak?all=1&functions=hutes-futes");
  const goToBuilder = () =>
    navigate("/builder", { state: { functionIds: ["hutes-futes"] } });

  return (
    <motion.div
      key={location.key}
      className="min-h-screen bg-gradient-to-b from-white to-slate-50 text-[#0c1a26]"
      initial="hidden"
      animate="show"
    >
      {/* ===== HERO – egységes stílus (Home/Builder) ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
        <div className="absolute inset-0 opacity-25">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${"/img/t5.webp"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-20 md:pb-28 text-white">
          <motion.h1 className="text-3xl md:text-5xl font-bold drop-shadow-sm" variants={fadeUp}>
            Hűtés–Fűtés
          </motion.h1>
          <motion.p className="text-white/90 text-lg md:text-xl max-w-3xl mt-2" variants={fadeUp}>
            Okos termosztátokkal, zónásítással és szenzorokkal kényelmesebben,
            takarékosabban tarthatod az ideális hőmérsékletet – automatikusan.
          </motion.p>

          <motion.div className="mt-7 flex flex-wrap gap-3" variants={fadeUp}>
            <button
              onClick={goToFilteredProducts}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 font-semibold"
            >
              Termékek (előszűrve) →
            </button>
            <button
              onClick={goToBuilder}
              className="inline-flex items-center justify-center rounded-xl border border-white/40 hover:bg-white hover:text-[#0c1a26] px-5 py-3 font-semibold"
            >
              Kezd el a Builderben
            </button>
            <button
              onClick={() => navigate("/funkciok")}
              className="inline-flex items-center justify-center rounded-xl border border-white/40/60 hover:bg-white/10 px-5 py-3 font-semibold"
            >
              Vissza a funkciókhoz
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===== TARTALOM ===== */}
      <div className="max-w-7xl mx-auto px-6 py-10 space-y-14">

        {/* Bevezető + fő kép (a korábbi oldal fő illusztrációja) */}
        <section className="grid md:grid-cols-[1.3fr_1fr] gap-6 items-stretch">
          <motion.div
            className="bg-white/90 backdrop-blur rounded-2xl border p-5 md:p-6"
            variants={fadeUp}
          >
            <h2 className="text-2xl font-semibold">Mi ez?</h2>
            <p className="text-gray-700 mt-2">
              A rendszer <strong>helyetted gondolkodik</strong>: esti kényelmi hőmérséklet,
              napközben takarék, ablaknyitás-érzékelés, jelenlét és zónásítás – stabil komfort,
              alacsonyabb számlák.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Komfort</div>
                <div className="text-sm text-gray-700">
                  Állandó, kellemes hőmérséklet zavaró ingadozás nélkül.
                </div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Energiamegtakarítás</div>
                <div className="text-sm text-gray-700">
                  Időzítés, jelenlét, ablaknyitás-érzékelés – okos takarék.
                </div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Zónásítás</div>
                <div className="text-sm text-gray-700">
                  Helyiségenként külön célhőmérséklet és ütemezés.
                </div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Távvezérlés</div>
                <div className="text-sm text-gray-700">
                  Appból és hangasszisztenssel, bárhonnan.
                </div>
              </div>
            </div>
          </motion.div>

          {/* FŐ KÉP – /img/base/hutes-futes.webp */}
          <motion.figure
            className="rounded-2xl border overflow-hidden bg-white"
            variants={fadeUp}
          >
            <img
              src="/img/base/hutes-futes.webp"
              alt="Hűtés–Fűtés – fő illusztráció"
              className="w-full h-full object-contain"
              loading="lazy"
            />
            <figcaption className="px-4 py-2 text-xs text-gray-600">
              Hűtés–Fűtés – termosztátok, szelepek, klímavezérlők.
            </figcaption>
          </motion.figure>
        </section>

        {/* Videók – rövid MP4-ek, loop + leírás (a korábbi verzió mintájára) */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-3" variants={fadeUp}>
            Hogyan néz ki működés közben?
          </motion.h2>
          <p className="text-sm text-gray-600 mb-4">
            Rövid demók: esti komfort és szellőztetéskori takarék – a videók halkan, végtelenítve futnak.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Video #1 */}
            <motion.figure
              className="rounded-2xl overflow-hidden border bg-black"
              variants={fadeUp}
            >
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/okos-termosztat-1.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Esti komfort, automatikusan:</strong> pihenéshez ideális hőmérséklet – kevesebb állítgatás.
              </figcaption>
            </motion.figure>

            {/* Video #2 */}
            <motion.figure
              className="rounded-2xl overflow-hidden border bg-black"
              variants={fadeUp}
            >
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/okos-termosztat-2.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Okos megtakarítás szellőztetéskor:</strong> az ablaknyitást észlelve takarékra vált.
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* Mikor használd? */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Mikor használd?
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Napi rutinban</h3>
              <p className="text-gray-700 text-sm">
                Ébredés előtt finom felfűtés, távozáskor visszavétel, érkezésre visszamelegítés/hűtés.
              </p>
            </motion.div>
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Ablaknyitásnál</h3>
              <p className="text-gray-700 text-sm">
                Nyitott ablaknál a rendszer szünetel, majd automatikusan folytatja.
              </p>
            </motion.div>
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Zónásított terekben</h3>
              <p className="text-gray-700 text-sm">
                Nappali, háló, gyerekszoba külön célhőmérséklettel – csak ott fűt/hűt, ahol kell.
              </p>
            </motion.div>
            <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Nyaralás alatt</h3>
              <p className="text-gray-700 text-sm">
                Takarmód vagy fagyvédelem, érkezés előtt visszakapcsolás kényelmes szintre.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Előnyök és korlátok */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Előnyök és korlátok
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div className="bg-green-50 border border-green-200 rounded-xl p-4" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Előnyök</h3>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>Kényelmes, energiatudatos működés</li>
                <li>Zónásítás, időzítés, jelenlét-érzékelés</li>
                <li>Távvezérlés és hangasszisztens integráció</li>
              </ul>
            </motion.div>
            <motion.div className="bg-red-50 border border-red-200 rounded-xl p-4" variants={fadeUp}>
              <h3 className="font-semibold mb-2">Korlátok</h3>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>Néhány rendszerhez központ vagy relémodul szükséges</li>
                <li>Kompatibilitás fűtési/hűtési típustól függ (kazán, hőszivattyú, klíma)</li>
                <li>Telepítéskor szakértelem javasolt</li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Ajánlott termékek – funkció szerint előszűrve (ProductGrid) */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Ajánlott termékek
          </motion.h2>
          <div className="rounded-2xl border bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
            <ProductGrid
              activeFilters={{ functionId: "hutes-futes" }}
              setActiveFilters={() => {}}
              categoriesMap={{}}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={goToFilteredProducts}
              className="rounded-xl bg-blue-600 text-white px-5 py-3 hover:bg-blue-700"
            >
              Termékek teljes listája →
            </button>
            <button
              onClick={goToBuilder}
              className="rounded-xl border px-5 py-3 hover:bg-gray-50"
            >
              Hűtés–Fűtés építése a Builderben
            </button>
          </div>
        </section>
      </div>

      {/* Sticky mobil CTA */}
      <div className="md:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
        <motion.button
          className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg"
          onClick={goToFilteredProducts}
          aria-label="Ugrás a hűtés–fűtés termékekhez"
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: 1.02 }}
        >
          Hűtés–Fűtés termékek →
        </motion.button>
      </div>
    </motion.div>
  );
}
